/**
 * BasicInfoSection Component
 * 협력사 기본 정보 표시 섹션
 */
import React, { useEffect, useRef, useState } from "react";
import { SupplierInfo } from "../types/supplierInfo.types";
import { SaveIcon } from '@/shared/components/icons/Icons';
import { getItem } from '@/shared/utils/storage';
import { commonCodeApi } from '@/shared/api/commonCodeApi';
import { CommonCodeDto } from '@/shared/types/commonCode.types';

interface BasicInfoSectionProps {
  supplierInfo: SupplierInfo;
  saving: boolean;
  onFieldChange: (field: keyof SupplierInfo, value: string) => void;
  onSave: () => void;
}

const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  supplierInfo,
  saving,
  onFieldChange,
  onSave,
}) => {
  // 사용자 타입 확인 (관리자만 협력사 타입 수정 가능)
  const userType = getItem<string>('USER_TYPE');
  const isAdmin = userType === 'HEADQUARTERS';

  const [gradeCodes, setGradeCodes] = useState<CommonCodeDto[]>([]);
  const hasFetchedGradeCodes = useRef(false);

  useEffect(() => {
    if (hasFetchedGradeCodes.current) return;
    let isCancelled = false;
    const fetchGradeCodes = async () => {
        try {
            const response = await commonCodeApi.getCommonCodeList('SUPP_GRADE');
            if (!isCancelled) {
                setGradeCodes(response.codes || []);
                hasFetchedGradeCodes.current = true;
            }
        } catch {
            // 등급 코드 조회 실패 시 기본값 유지
        }
    };
    fetchGradeCodes();
    return () => { isCancelled = true; };
  }, []);

  return (
    <div className="supplier-section">
      <h2 className="section-title">기본 정보</h2>
      <div className="info-list">
          {/* 편집 가능한 필드: 휴대폰 번호 */}
          <div className="info-row">
              <span className="info-label">휴대폰 번호:</span>
              <span className="info-value">{supplierInfo.mobilePhone}</span>
          </div>
          <div className="info-row">
              <span className="info-label">협력사 주소:</span>
              <input
                  type="text"
                  className="info-input"
                  value={supplierInfo.address || ""}
                  onChange={(e) => onFieldChange("address", e.target.value)}
                  placeholder="협력사 주소를 입력하세요"
              />
          </div>
          <div className="info-row">
              <span className="info-label">협력사명:</span>
              <span className="info-value">{supplierInfo.supplierName}</span>
          </div>
          <div className="info-row">
              <span className="info-label">협력사 아이디:</span>
              <span className="info-value">{supplierInfo.supplierId}</span>
          </div>
          <div className="info-row">
              <span className="info-label">협력사 등급:</span>
              {isAdmin ? (
                  <select
                      className="info-input"
                      value={supplierInfo.suppLvl || ''}
                      onChange={(e) => onFieldChange('suppLvl', e.target.value)}
                  >
                      <option value="">선택하세요</option>
                      {gradeCodes.map((grade) => (
                          <option key={grade.comCode} value={grade.comCode}>
                              {grade.comCdName}
                          </option>
                      ))}
                  </select>
              ) : (
                  <span className="info-value">{supplierInfo.supplierGrade || '-'}</span>
              )}
          </div>
          <div className="info-row">
              <span className="info-label">협력사 세트수:</span>
              {isAdmin ? (
                  <input
                      type="number"
                      className="info-input"
                      value={supplierInfo.slotSetCnt || ''}
                      onChange={(e) => onFieldChange('slotSetCnt', e.target.value)}
                      placeholder="세트수를 입력하세요"
                      min="0"
                  />
              ) : (
                  <span className="info-value">{supplierInfo.slotSetCnt || '-'}</span>
              )}
          </div>
          <div className="info-row">
            <span className="info-label">상태:</span>
            <span className="info-value">{supplierInfo.status}</span>
        </div>
          <div className="info-row">
            <span className="info-label">등록일:</span>
            <span className="info-value">{supplierInfo.registrationDate}</span>
        </div>
          <div className="info-row">
            <span className="info-label">운영종료일:</span>
            <span className="info-value">
              {supplierInfo.operationEndDate || "-"}
          </span>
        </div>
          <div className="info-row">
            <span className="info-label">전화번호:</span>
            <input
            type="text"
            className="info-input"
            value={supplierInfo.mobilePhone}
            onChange={(e) => onFieldChange("mobilePhone", e.target.value)}
            placeholder="휴대폰 번호를 입력하세요"
          />
        </div>
        <div className="info-row">
          <span className="info-label">협력사 타입:</span>
          {isAdmin ? (
            <select
              className="info-input"
              value={supplierInfo.suppType === '가맹' ? 'F' :
                     supplierInfo.suppType === '직영' ? 'D' :
                     supplierInfo.suppType === 'F' ? 'F' :
                     supplierInfo.suppType === 'D' ? 'D' : 'D'}
              onChange={(e) => {
                onFieldChange('suppType', e.target.value);
              }}
            >
              <option value="D">직영</option>
              <option value="F">가맹</option>
            </select>
          ) : (
            <span className="info-value">{supplierInfo.suppType}</span>
          )}
        </div>
        <div className="info-row">
          <span className="info-label">배달권역:</span>
          <span className="info-value">{supplierInfo.deliveryArea}</span>
        </div>
      </div>

      {/* 저장 버튼 */}
      <div className="section-actions">
        <button className="btn btn-primary" onClick={onSave} disabled={saving}>
          <SaveIcon width={16} height={16} />
          <span>{saving ? "저장 중..." : "저장"}</span>
        </button>
      </div>
    </div>
  );
};

export default BasicInfoSection;
